import { ROLES, ROLE_IDS } from '@efexteam/permission-manager';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import {
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useInfoCard from './hooks/useInfoCard';
import InfoCardStyles from './InfoCard.style';
import { IInfoCard } from './types';

const InfoCard = ({
  teamMemberData,
  teamMemberControlStatus,
  updateMemberStatus,
  setOpenDeleteModal,
}: IInfoCard) => {
  const { t } = useTranslation();
  const { naturalPerson, platformRole } = teamMemberData || {};
  const { email } = naturalPerson || {};
  const { id: platformRoleId } = platformRole || {};

  const { anchorEl, open, handleClick, handleClose } = useInfoCard();

  const renderRow = (
    text: string,
    element: React.ReactNode | string,
    isHidden = false
  ) => (
    <Stack
      className={`${InfoCardStyles.rowWrapper} ${
        isHidden ? InfoCardStyles.hiddenRow : ''
      }`}
    >
      <Typography variant="body2" className={InfoCardStyles.rowTitle}>
        {text}
      </Typography>
      {typeof element === 'string' ? (
        <Typography variant="body2" className={InfoCardStyles.rowValue}>
          {element}
        </Typography>
      ) : (
        element
      )}
    </Stack>
  );

  return (
    <Grid item xs={12} md={6}>
      <Card>
        <CardHeader
          title={t('editMmbInfoTitle')}
          action={
            ROLE_IDS[platformRoleId as unknown as keyof typeof ROLE_IDS] !==
              ROLES.OWNER && (
              <>
                <IconButton onClick={handleClick}>
                  <MoreVertOutlinedIcon />
                </IconButton>
                <Menu
                  id="team-member-edit-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'center',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem
                    className={InfoCardStyles.deleteAction}
                    onClick={() => setOpenDeleteModal(true)}
                  >
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('editMmbInfoDelete')}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            )
          }
          className={InfoCardStyles.cardHeader}
        />
        <CardContent>
          <Stack gap={1}>
            {renderRow(
              t('editMmbInfoStatus'),
              <FormControlLabel
                control={
                  <Switch
                    disabled={
                      ROLE_IDS[
                        platformRoleId as unknown as keyof typeof ROLE_IDS
                      ] === ROLES.OWNER
                    }
                    checked={teamMemberControlStatus}
                    className={InfoCardStyles.switchWrapper}
                    onChange={(e) => updateMemberStatus(e.target.checked)}
                  />
                }
                labelPlacement="start"
                label={
                  <Typography
                    variant="body2"
                    className={InfoCardStyles.switchLabel}
                  >
                    {t(
                      teamMemberControlStatus
                        ? 'editMmbInfoActive'
                        : 'editMmbInfoInactive'
                    )}
                  </Typography>
                }
              />,
              true // TODO: remove this if/when we can inactive users
            )}
            {renderRow(t('editMmbInfoEmail'), email)}
            {renderRow(
              t('editMmbInfoRole'),
              t(
                `member${
                  ROLE_IDS[
                    platformRoleId as unknown as keyof typeof ROLE_IDS
                  ]?.toUpperCase() || ''
                }`
              )
            )}
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default InfoCard;
